import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "../lib/supabase";
import type { YearOverview } from "../types/core";

// =============================================================================
// useYearOverview — Fetch Year-level aggregated metrics for Year View Tab
// =============================================================================
// PURPOSE: Calls get_year_overview RPC and returns pre-aggregated monthly data
// for the Year View Tab on /admin/dashboard.
//
// dayStart / dayEnd (P11): when 1/31 (defaults), behavior is byte-for-byte equivalent
// to v109. Other values restrict every month's aggregation to transactions whose
// anchored day-of-month falls in [dayStart, dayEnd] (inclusive, org tz). REQUIRED
// params — TypeScript catches every miss at compile time, no silent wrong defaults.
//
// RETURNS: { data, isLoading, error, refetch }
// =============================================================================

export function useYearOverview(
  organizationId: string | null | undefined,
  year: number,
  dayStart: number,
  dayEnd: number
): {
  data: YearOverview | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
} {
  const [data, setData] = useState<YearOverview | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const fetchData = useCallback(async () => {
    if (!organizationId || !supabase) {
      setData(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    setError(null);

    try {
      const { data: rpcData, error: rpcError } = await supabase.rpc("get_year_overview", {
        p_organization_id: organizationId,
        p_year: year,
        p_day_start: dayStart,
        p_day_end: dayEnd,
      });

      if (controller.signal.aborted) return;

      if (rpcError) {
        setError(rpcError.message);
        setData(null);
      } else {
        setData(rpcData as YearOverview);
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      setError(err instanceof Error ? err.message : "Failed to fetch year overview");
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [organizationId, year, dayStart, dayEnd]);

  useEffect(() => {
    fetchData();
    return () => {
      abortRef.current?.abort();
    };
  }, [fetchData]);

  return { data, isLoading, error, refetch: fetchData };
}
